import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Coins, CreditCard, AlertCircle, Check, RefreshCw, Loader2 } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REASON_KEY = (reason) => {
  if (!reason) return "fallback";
  if (["tavily_search", "firecrawl_crawl", "llm_call", "pack_change", "manual_grant", "monthly_reset", "smoke_test"].includes(reason)) {
    return reason;
  }
  return "fallback";
};

function pickLabel(labels, lang, fallback) {
  if (!labels) return fallback;
  return (labels[lang] && labels[lang].name) || (labels.en && labels.en.name) || fallback;
}
function pickDesc(labels, lang) {
  if (!labels) return "";
  return (labels[lang] && labels[lang].description) || (labels.en && labels.en.description) || "";
}

function daysUntil(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.round((t - Date.now()) / (24 * 60 * 60 * 1000)));
}

function fmtDateTime(iso, lang) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts = { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" };
  try {
    return new Intl.DateTimeFormat(lang || "en", opts).format(d);
  } catch (_e) {
    return d.toISOString();
  }
}

export default function CreditsSettings() {
  const { t, i18n } = useTranslation(["credits", "common"]);
  const { user } = useAuth();
  const lang = (i18n.language || "en").split("-")[0];
  const role = user?.role;
  const canChange = role === "owner" || role === "admin";

  const [balance, setBalance] = useState(null);
  const [packs, setPacks] = useState([]);
  const [txs, setTxs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingPackId, setPendingPackId] = useState(null);
  const [switching, setSwitching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [bal, list, transactions] = await Promise.all([
        api.get("/credits/me"),
        api.get("/credits/packs"),
        api.get("/credits/transactions?limit=50"),
      ]);
      setBalance(bal.data);
      setPacks(Array.isArray(list.data) ? list.data : []);
      setTxs(Array.isArray(transactions.data) ? transactions.data : []);
    } catch (e) {
      setError(t("credits:errors.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const pendingPack = useMemo(
    () => packs.find((p) => (p.id || p._id) === pendingPackId) || null,
    [packs, pendingPackId]
  );

  const handleConfirmSwitch = useCallback(async () => {
    if (!pendingPack) return;
    setSwitching(true);
    try {
      const id = pendingPack.id || pendingPack._id;
      const { data } = await api.post("/credits/select-pack", { pack_id: id });
      setBalance(data);
      const transactions = await api.get("/credits/transactions?limit=50");
      setTxs(Array.isArray(transactions.data) ? transactions.data : []);
      toast.success(
        t("credits:packs.switched_toast", {
          pack: pickLabel(pendingPack.labels, lang, pendingPack.tier),
        })
      );
      setPendingPackId(null);
    } catch (e) {
      toast.error(t("credits:errors.switch_failed"));
    } finally {
      setSwitching(false);
    }
  }, [pendingPack, lang, t]);

  const used = balance ? Math.max(0, (balance.monthly_allowance || 0) - (balance.current_balance || 0)) : 0;
  const usedPct = balance && balance.monthly_allowance > 0
    ? Math.min(100, Math.round((used / balance.monthly_allowance) * 100))
    : 0;
  const remainingPct = 100 - usedPct;
  const resetDays = balance ? daysUntil(balance.next_reset_estimate) : null;

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8" data-testid="credits-settings-page">
      <header className="mb-8">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">
          {t("credits:title")}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">{t("credits:subtitle")}</p>
      </header>

      {error && (
        <div
          className="mb-6 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
          data-testid="credits-load-error"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
          <span>{error}</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="ml-auto h-7 px-2 text-amber-900 hover:bg-amber-100"
            onClick={load}
          >
            <RefreshCw className="mr-1 h-3.5 w-3.5" strokeWidth={1.75} />
            {t("common:retry", { defaultValue: "Retry" })}
          </Button>
        </div>
      )}

      {/* Balance card */}
      <section className="grid gap-6 md:grid-cols-3">
        <div
          className="md:col-span-2 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          data-testid="credits-balance-card"
        >
          {loading || !balance ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-10 w-48" />
              <Skeleton className="h-3 w-64" />
              <Skeleton className="h-2 w-full" />
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <Coins className="h-4 w-4" strokeWidth={1.75} />
                {t("credits:balance.label")}
              </div>
              <div
                className="mt-2 flex items-baseline gap-3"
                data-testid="credits-balance-value"
              >
                <span className="font-display text-4xl font-semibold tabular-nums tracking-tight text-slate-900">
                  {(balance.current_balance || 0).toLocaleString(lang)}
                </span>
                <span className="text-sm text-slate-500">
                  / {(balance.monthly_allowance || 0).toLocaleString(lang)}
                </span>
              </div>
              <p className="mt-2 text-sm text-slate-600">
                {t("credits:balance.of_allowance", {
                  used: used.toLocaleString(lang),
                  allowance: (balance.monthly_allowance || 0).toLocaleString(lang),
                })}
                {" · "}
                {t("credits:balance.remaining_pct", { pct: remainingPct })}
              </p>
              <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-indigo-500 transition-all"
                  style={{ width: `${usedPct}%` }}
                  data-testid="credits-usage-bar"
                />
              </div>
            </>
          )}
        </div>

        <div
          className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
          data-testid="credits-meta-card"
        >
          {loading || !balance ? (
            <div className="space-y-3">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-28" />
            </div>
          ) : (
            <>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                {t("credits:current_pack.label")}
              </p>
              <p className="mt-1 text-lg font-semibold text-slate-900" data-testid="credits-current-pack">
                {pickLabel(
                  (packs.find((p) => (p.id || p._id) === balance.pack_id) || {}).labels,
                  lang,
                  balance.pack_id
                )}
              </p>
              <p className="mt-1 text-sm text-slate-600">
                {t("credits:current_pack.monthly_credits", {
                  count: (balance.monthly_allowance || 0).toLocaleString(lang),
                })}
              </p>
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {t("credits:balance.next_reset")}
                </p>
                <p className="mt-1 text-sm text-slate-700" data-testid="credits-next-reset">
                  {resetDays === null
                    ? "—"
                    : resetDays === 0
                      ? t("credits:balance.next_reset_today")
                      : t("credits:balance.next_reset_relative", { days: resetDays })}
                </p>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Pack catalog */}
      <section className="mt-10" data-testid="credits-packs-section">
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("credits:packs.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t("credits:packs.subtitle")}</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {loading
            ? [1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 w-full rounded-2xl" />
              ))
            : packs.map((p) => {
                const id = p.id || p._id;
                const isCurrent = balance && balance.pack_id === id;
                const description = pickDesc(p.labels, lang);
                return (
                  <div
                    key={id}
                    className={`relative rounded-2xl border bg-white p-5 shadow-sm transition-colors ${
                      isCurrent ? "border-indigo-300 ring-2 ring-indigo-100" : "border-slate-200"
                    }`}
                    data-testid={`credits-pack-card-${id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg font-semibold text-slate-900">
                          {pickLabel(p.labels, lang, p.tier)}
                        </p>
                        <p className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                          {p.tier}
                        </p>
                      </div>
                      {isCurrent && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-medium text-indigo-700">
                          <Check className="h-3 w-3" strokeWidth={2} />
                          {t("credits:packs.current")}
                        </span>
                      )}
                    </div>
                    <p className="mt-3 text-2xl font-semibold tabular-nums text-slate-900">
                      {(p.monthly_credits || 0).toLocaleString(lang)}{" "}
                      <span className="text-sm font-normal text-slate-500">
                        / {t("credits:current_pack.monthly_credits", { count: "" }).replace(/\s*\/.*/, "").trim() || "mo"}
                      </span>
                    </p>
                    {description && (
                      <p className="mt-3 text-xs text-slate-500">{description}</p>
                    )}
                    <div className="mt-5">
                      {isCurrent ? (
                        <Button disabled variant="outline" className="w-full" data-testid={`credits-pack-current-${id}`}>
                          {t("credits:packs.current")}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          disabled={!canChange}
                          onClick={() => setPendingPackId(id)}
                          className="w-full bg-slate-900 text-white hover:bg-slate-800"
                          data-testid={`credits-pack-choose-${id}`}
                        >
                          {t("credits:packs.choose")}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
        </div>
      </section>

      {/* Transactions */}
      <section className="mt-10" data-testid="credits-transactions-section">
        <h2 className="font-display text-xl font-semibold tracking-tight text-slate-900">
          {t("credits:transactions.title")}
        </h2>
        <p className="mt-1 text-sm text-slate-600">{t("credits:transactions.subtitle")}</p>
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-6 space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <Skeleton className="h-4 w-3/4" />
            </div>
          ) : txs.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="credits-tx-empty">
              {t("credits:transactions.empty")}
            </div>
          ) : (
            <table className="w-full text-sm" data-testid="credits-tx-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">{t("credits:transactions.th_when")}</th>
                  <th className="px-4 py-3 font-medium">{t("credits:transactions.th_reason")}</th>
                  <th className="px-4 py-3 text-right font-medium">{t("credits:transactions.th_amount")}</th>
                  <th className="px-4 py-3 text-right font-medium">{t("credits:transactions.th_balance")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {txs.map((tx) => {
                  const key = REASON_KEY(tx.reason);
                  const reasonLabel =
                    key === "fallback"
                      ? t("credits:transactions.reason.fallback", { raw: tx.reason })
                      : t(`credits:transactions.reason.${key}`);
                  const positive = tx.amount > 0;
                  return (
                    <tr key={tx.id || tx._id} data-testid={`credits-tx-row-${tx.id || tx._id}`}>
                      <td className="px-4 py-3 text-slate-600">{fmtDateTime(tx.created_at, lang)}</td>
                      <td className="px-4 py-3 text-slate-700">{reasonLabel}</td>
                      <td className={`px-4 py-3 text-right tabular-nums font-medium ${positive ? "text-emerald-700" : "text-slate-900"}`}>
                        {positive ? "+" : ""}
                        {(tx.amount || 0).toLocaleString(lang)}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {(tx.balance_after || 0).toLocaleString(lang)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {/* Confirm switch */}
      <Dialog open={!!pendingPackId} onOpenChange={(open) => !open && !switching && setPendingPackId(null)}>
        <DialogContent data-testid="credits-confirm-switch-dialog">
          <DialogHeader>
            <DialogTitle>
              {t("credits:packs.confirm_switch_title", {
                pack: pendingPack ? pickLabel(pendingPack.labels, lang, pendingPack.tier) : "",
              })}
            </DialogTitle>
            <DialogDescription>
              {t("credits:packs.confirm_switch_body", {
                credits: pendingPack ? (pendingPack.monthly_credits || 0).toLocaleString(lang) : "0",
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={switching}
              onClick={() => setPendingPackId(null)}
              data-testid="credits-confirm-cancel"
            >
              {t("credits:packs.cancel")}
            </Button>
            <Button
              type="button"
              disabled={switching}
              onClick={handleConfirmSwitch}
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="credits-confirm-switch"
            >
              {switching ? <Loader2 className="h-4 w-4 animate-spin" /> : t("credits:packs.confirm_cta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
